import * as Api from "utils/api/api";
import * as OdpApi from "utils/api/odp.api";
import {
    getTaskDetails,
    removeSelectedDocument,
    updateTaskDetailsAfterAction,
} from "components/taskdetails-remake/taskDetails.action";
import { showNotification } from "components/notifications/notification.action";
import {seamlessLoadTasks} from "components/mytasks/myTasks.action";
import {ForwardActionData, DeleteActionData, AddActionData, RequestReviewActionData, ReassignActionData, ReassignReviewActionData, CancelDocumentActionData, ReviewTaskActionData, ApproveDirectlyActionData, RejectDirectlyActionData, RejectActionData, EmailActionData, SendForApprovalActionData, SubstituteActionData,  AddCostUnitNodeActionData, LookupCostCentersActionData, WorkflowHistoryActionData} from "types/modals";
import { Dispatch } from "redux";
import { TASK_ACTIONS } from "utils/constants";
import { mapErrorMessage } from "utils/errorHandle.function";
import {replacePlaceholderInMessage} from "utils/valueFormatter.function";
import {userSettingsUpdated} from "components/usercontext/user.action";

export function deleteAttachment( data: DeleteActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.deleteAttachment(data.key, data.collectionGuid, data.id).then(function() {
                dispatch(removeSelectedDocument());
                dispatch(getTaskDetails(data.key));
                dispatch(showNotification(data.deleteMessage));
                successCallback();
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function addAttachment(data: AddActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.addAttachment(data.key, encodeURIComponent(data.title), encodeURIComponent(data.comment), data.file).then(function (response: any) {
                let documentName = JSON.parse(response)?.text || '';
                dispatch(updateTaskDetailsAfterAction(data.key, TASK_ACTIONS.ADD_ATTACHMENT));
                dispatch(showNotification(documentName + " " + data.addMessage));
                successCallback();
            },
            function (error: any) {
                errorCallback({ fileTooBigMessage: mapErrorMessage(error)});
            }
        );
    }
}

export function forwardTask(data: ForwardActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.forwardTask(data.key,data.comment, data.recipient, data.uid).then(function () {
                dispatch(showNotification(data.forwardMessage));
                successCallback();
            },
            function (error:any) {
                errorCallback({ error: error });
            });
    }
}

export function postponeTasks(taskKeys, comment, dayFields, postponeMessage, successCallback, errorCallback) {
    return function(dispatch: Dispatch<any>) {
        Api.postponeTask(taskKeys, comment, dayFields.thisDay, dayFields.daysFromNow, dayFields.daysBefore).then(function(response) {
                if (response.success > 0) {
                    dispatch(showNotification(postponeMessage));
                    if (response.total !== response.success) {
                        errorCallback();

                    } else {
                        successCallback();
                        dispatch(seamlessLoadTasks());
                    }
                } else {
                    errorCallback();
                }

            },
            function(error) {
                errorCallback({ error: error });
            },
        );
    }
}


export function postponeTasksByUID(uids: string[], comment: string, newTaskDueDate: string, postponeMessage: string, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        // One request per task: POST /tasks/{uid}/postpone
        Promise.allSettled(uids.map((uid) => Api.postponeTaskByUID(uid, newTaskDueDate, comment)))
            .then(function(results) {
                const success = results.filter((result) => result.status === "fulfilled").length;

                if (success > 0) {
                    dispatch(showNotification(postponeMessage));
                    if (success !== uids.length) {
                        errorCallback();
                    } else {
                        successCallback();
                        dispatch(seamlessLoadTasks());
                    }
                } else {
                    errorCallback();
                }
            });
    }
}


export function reassignTasks(data: ReassignActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.OverviewReassign(data.keys, data.comment, data.recipient).then(function(response: any) {
                dispatch(showNotification(response.message));
                successCallback();
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}


export function cancelDocument(data: CancelDocumentActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.cancelDocument(data.companyId, data.key, data.comment).then(function(response: { message: string }) {
                dispatch(showNotification(response.message));
                successCallback();
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function requestReview(data: RequestReviewActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.requestReviewTask(data.key, data.recipient, data.comment, data.uid).then(function (response) {
                const withPlaceholder = replacePlaceholderInMessage(response.message, data.taskDescription);
                dispatch(showNotification(withPlaceholder));
                successCallback();
            },
            function (error:any) {
                errorCallback({ error: error });
            });
    }
}

/**
 * the review task the new reviewer gets (newTaskUid) is readable only a moment after this returns,
 * so the caller reloads the list of reviews rather than reading it
 */
export function reassignReview(data: ReassignReviewActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.reassignReviewTask(data.uid, data.recipient, data.comment).then(function (response: { message?: string }) {
                if (response?.message) dispatch(showNotification(response.message));
                successCallback();
            },
            function (error: any) {
                errorCallback({ error: error });
            });
    }
}

export function reviewTask(data: ReviewTaskActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.reviewTask(data.key, data.comment, data.uid).then(function (response: any) {
                const withPlaceholder = replacePlaceholderInMessage(response.message, data.taskDescription);
                dispatch(showNotification(withPlaceholder));
                successCallback();
            },
            function (error:any) {
                errorCallback({ error: error });
            });
    }
}

export function rejectTask(data: RejectActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.rejectTask(data.key, data.comment, data.uid).then(function (response: any) {
                const withPlaceholder = replacePlaceholderInMessage(response.message, data.taskDescription);
                dispatch(showNotification(withPlaceholder));
                successCallback();
            },
            function (error:any) {
                errorCallback({ error: error });
            });
    }
}

export function approveDocumentDirectly(data: ApproveDirectlyActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.approveDocumentDirectly(data.comment, data.taskIds, data.companyId).then(function(response: { message: string }) {
                dispatch(showNotification(response.message));
                successCallback();
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function fetchDraftComment(showComments: boolean, tasks: Array<Record<string, any>>, onSuccess: (text: string) => void) {
    return function() {
        if (!showComments || tasks.length !== 1 || !tasks[0]?.documentUid) {
            return;
        }
        Api.getDraftComment(tasks[0].documentUid).then(function(response: { text?: string }) {
            if (response?.text) {
                onSuccess(response.text);
            }
        }).catch(function(error: any) {
            console.error("Failed to fetch draft comment", error);
        });
    }
}

export function updateUserSettings(userSettings: Record<string, any>, companyId: string) {
    return function(dispatch: Dispatch<any>) {
        Api.updateUserSettings(userSettings, companyId).then(function() {
            // keep the store in sync, so the new preference is applied without reloading the app
            dispatch(userSettingsUpdated(userSettings));
        }).catch(function(error: any) {
            console.error("Failed to update user settings", error);
        });
    }
}

export function rejectDocumentDirectly(data: RejectDirectlyActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.rejectDocumentDirectly(data.comment, data.taskIds, data.companyId).then(function(response: { message: string }) {
                dispatch(showNotification(response.message));
                successCallback();
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function emailTask(data: EmailActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        const apiCall = data.sendDocument
            ? Api.emailDocument(data.key, data.to, data.comment, data.subject, data.sendAttachment, data.sendCopy, data.cc, data.bcc, data.companyId)
            : Api.emailTask(data.key, data.to, data.comment, data.subject, data.sendAttachment, data.sendCopy, data.cc, data.bcc);

        apiCall.then(function(response: { message: string }) {
                dispatch(showNotification(response.message));
                successCallback();
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function sendDocumentForApproval(data: SendForApprovalActionData, successCallback: Function, errorCallback: Function) {
    return function(dispatch: Dispatch<any>) {
        Api.sendDocumentForApproval(data.comment, data.taskIds, data.companyId).then(function(response: { message: string }) {
                dispatch(showNotification(response.message));
                successCallback();
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function saveCostUnitNodes(data: AddCostUnitNodeActionData, successCallback: Function, errorCallback: Function) {
    return function() {
        Api.saveCostUnitNodes(data.companyId, data.nodes, data.treeVersion).then(function(response: any) {
                successCallback(response);
            },
            function(error: any) {
                if (error?.errorcode === 409 && data.getApproversTreeRoot) {
                    data.getApproversTreeRoot(data.companyId);
                }
                errorCallback({ error: error });
            }
        );
    }
}

export function lookupCostCenters(data: LookupCostCentersActionData, dimension: number, successCallback: Function, errorCallback: Function) {
    return function() {
        Api.lookupCostCenters(data, dimension).then(function(response: any) {
                successCallback(response);
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function saveSubstitute(data: SubstituteActionData, successCallback: Function, errorCallback: Function) {
    return function() {
        Api.saveSubstitute(data).then(function() {
                successCallback();
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function getSubstituteForUser(userId: string | number, successCallback: Function, errorCallback: Function) {
    return function() {
        Api.getSubstitute(userId).then(function(response: any) {
                successCallback(response);
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function validateSubstitute(data: SubstituteActionData, successCallback: Function, errorCallback: Function) {
    return function() {
        Api.validateSubstitute(data).then(function(response: any) {
                successCallback(response);
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function getWorkflowsHistory(data: WorkflowHistoryActionData, successCallback: Function, errorCallback: Function) {
    return function() {
        const params: Record<string, any> = {
            page: 1,
            rows: 20,
        };
        if (data.documentType) {
            params.documentType = data.documentType;
        }
        if (data.ruleConfigId) {
            params.ruleConfigId = data.ruleConfigId;
        }
        Api.getWorkflowHistory(data.companyId, params).then(function(response: any) {
                successCallback(response);
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function loadMoreWorkflowsHistory(data: WorkflowHistoryActionData, successCallback: Function, errorCallback: Function) {
    return function() {
        const page = data.page ?? 1;
        const params: Record<string, any> = {
            page,
            rows: data.rows || 20,
        };
        if (data.documentType) {
            params.documentType = data.documentType;
        }
        if (data.ruleConfigId) {
            params.ruleConfigId = data.ruleConfigId;
        }
        Api.getWorkflowHistory(data.companyId, params).then(function(response: any) {
                successCallback(response);
            },
            function(error: any) {
                errorCallback({ error: error });
            }
        );
    }
}

export function redirectToLogin(successCallback: Function, errorCallback: Function) {
    return function() {
        try {
            // redirect to odp - do not navigate through /logout, because we'd like to help the user continue
            // with their work and visiting logout would clean the store
            OdpApi.login();
            successCallback();
        } catch (error: any) {
            errorCallback({ error: error });
        }
    }
}

export function simulateWorkflowGraph(taskKey: string, companyId: string, successCallback: Function, errorCallback: Function) {
    return function() {
        Api._simulateWorkflowGraph(taskKey, companyId).then(
            function(response: any) {
                successCallback(response);
            },
            function(error: any) {
                errorCallback(error);
            },
        );
    }
}